import axios from "axios";
import { req } from "./req";
import type {
  CreateNodePoolParams,
  DescribeAutoScalingOptionParams,
  ModifyAutoScalingOptionParams,
  ModifyNodePoolDeleteProtectParams,
  ModifyNodePoolParams,
  ModifyScalingConfigurationParams,
  SetNodePoolAutoscalingParams,
  SetNodeScaleDownDisabledParams,
} from "./types/node-pool";
import type { WithRegion } from "../../../common/api/types";
import { AxiosRESTDataSource } from "../../../common/api/v2/rest-data-source";
import { axiosInstance } from "../../../app/axios";
import {
  DescribeAutoScalingOptionResponseSchema,
  ModifyAutoScalingOptionResponseSchema,
  SetNodePoolAutoscalingResponseSchema,
  SetNodeScaleDownDisabledResponseSchema,
} from "./schema/node-pool";
import type { KscResponseModel } from "../../../common/core/request";
import type {
  AddExistedNodesToNodePoolParams,
  AddExistedNodesToNodePoolResponse,
  GetNodePoolsParam,
  DescribeNodePoolActivitiesParam,
  GetNodePoolsResponse,
  DescribeNodePoolActivitiesResponse,
  DescribeNodePoolParam,
  DescribeNodePoolResponse,
  DescribeNodePoolNodesParam,
  DescribeNodePoolNodesResponse,
  ModifyNodePoolNameParam,
  ModifyNodePoolNameResponse,
  ModifyNodePoolLabelsParam,
  ModifyNodePoolLabelsResponse,
  ScalingUpParam,
  ScalingUpResponse,
  DeleteNodePoolParam,
  DeleteNodePoolResponse,
  ModifyNodePoolTaintsParam,
  ModifyNodePoolTaintsResponse,
  ModifyClusterDeleteProtectResponse,
  RemoveNodePoolNodeParam,
  RemoveNodePoolNodeResponse,
  DescribeExistedNodesForNodePoolParam,
  DescribeExistedNodesForNodePoolResponse,
} from "./types";

export function createNodePool(params: WithRegion<CreateNodePoolParams>) {
  return axios(
    req.post("CreateNodePool", params.Region, {
      data: params,
    }),
  );
}

export function modifyNodePool(params: WithRegion<ModifyNodePoolParams>) {
  return axios(
    req.post("ModifyNodePool", params.Region, {
      data: params,
    }),
  );
}

export async function getNodePools(params: GetNodePoolsParam) {
  return axios<KscResponseModel<GetNodePoolsResponse>>(
    req.post("GetNodePoolList", params.Region, {
      body: params,
    }),
  ).then(v => v.data || {});
}

export async function describeNodePool(params: DescribeNodePoolParam) {
  return axios<KscResponseModel<DescribeNodePoolResponse>>(
    req.post("DescribeNodePool", params.Region, {
      body: params,
    }),
  ).then(v => v.data || {});
}

export async function describeNodePoolNodes(params: DescribeNodePoolNodesParam) {
  return axios<KscResponseModel<DescribeNodePoolNodesResponse>>(
    req.post("GetNodePoolNodeList", params.Region, {
      body: params,
    }),
  ).then(v => v.data || {});
}

export async function modifyNodePoolName(params: ModifyNodePoolNameParam) {
  return axios<ModifyNodePoolNameResponse>(
    req.post("ModifyNodePoolName", params.Region, {
      body: params,
    }),
  ).then(v => v.data);
}

export async function modifyNodePoolLabels(params: ModifyNodePoolLabelsParam) {
  return axios<ModifyNodePoolLabelsResponse>(
    req.post("ModifyNodePoolLabels", params.Region, {
      body: params,
    }),
  ).then(v => v.data);
}

export async function modifyNodePoolTaints(params: ModifyNodePoolTaintsParam) {
  return axios<ModifyNodePoolTaintsResponse>(
    req.post("ModifyNodePoolTaints", params.Region, {
      body: params,
    }),
  ).then(v => v.data);
}

export async function scalingUp(params: ScalingUpParam) {
  return axios<ScalingUpResponse>(
    req.post("ScalingUp", params.Region, {
      body: params,
    }),
  ).then(v => v.data);
}

export async function deleteNodePool(params: DeleteNodePoolParam) {
  return axios<DeleteNodePoolResponse>(
    req.post("DeleteNodePool", params.Region, {
      body: params,
    }),
  ).then(v => v.data);
}

export async function describeNodePoolActivities(params: DescribeNodePoolActivitiesParam) {
  return axios<KscResponseModel<DescribeNodePoolActivitiesResponse>>(
    req.post("DescribeNodePoolActivities", params.Region, {
      body: params,
    }),
  ).then(v => v.data || {});
}

export async function addExistedNodesToNodePool(params: AddExistedNodesToNodePoolParams) {
  return axios<AddExistedNodesToNodePoolResponse>(
    req.post("NodePoolAddExistedNodes", params.Region, {
      data: params,
    }),
  );
}

export async function describeExistedNodesForNodePool(
  params: DescribeExistedNodesForNodePoolParam,
) {
  return axios<DescribeExistedNodesForNodePoolResponse>(
    req.post("DescribeExistedNodes", params.Region, {
      body: params,
    }),
  );
}

export async function removeNodePoolNode(params: RemoveNodePoolNodeParam) {
  return axios<RemoveNodePoolNodeResponse>(
    req.post("RemoveNodePoolNode", params.Region, {
      data: params,
    }),
  );
}

export function modifyScalingConfiguration(params: WithRegion<ModifyScalingConfigurationParams>) {
  return axios(
    req.post("ModifyScalingConfiguration", params.Region, {
      data: params,
    }),
  );
}

class AutoScalingApi extends AxiosRESTDataSource {
  baseURL = "/api/cluster";
  async describeAutoScalingOption(params: WithRegion<DescribeAutoScalingOptionParams>) {
    return this.post("", DescribeAutoScalingOptionResponseSchema, {
      Action: "DescribeAutoscalingOption",
      Region: params.Region,
      params,
    });
  }

  async modifyAutoScalingOption(params: WithRegion<ModifyAutoScalingOptionParams>) {
    return this.post("", ModifyAutoScalingOptionResponseSchema, {
      Action: "ModifyAutoscalingOption",
      Region: params.Region,
      data: params,
    });
  }

  async enableNodePoolAutoscaling(params: WithRegion<SetNodePoolAutoscalingParams>) {
    return this.post("", SetNodePoolAutoscalingResponseSchema, {
      Action: "SetNodePoolAutoscaling",
      Region: params.Region,
      params,
    });
  }

  async setNodeScaleDownDisabled(params: WithRegion<SetNodeScaleDownDisabledParams>) {
    return this.post("", SetNodeScaleDownDisabledResponseSchema, {
      Action: "SetNodeScaleDownDisabled",
      Region: params.Region,
      data: params,
    });
  }
}

export const autoScalingApi = new AutoScalingApi(axiosInstance);

export function modifyNodePoolDeleteProtect(params: WithRegion<ModifyNodePoolDeleteProtectParams>) {
  return axios<KscResponseModel<ModifyClusterDeleteProtectResponse>>(
    req.post("ModifyNodePoolDeleteProtect", params.Region, {
      data: params,
    }),
  );
}
